import { error } from '../app/logger.js';
import { HTMLControl } from '../controls/html_control.js';
import { FoodMenu } from '../data/food_menu.js';
import { listenToEvent } from '../util/listen_to_event.js';
import { window } from '../window.js';

import '../controls/spreadsheet_control.js';

class FoodMenuControl extends HTMLControl {
  static selectors = {
    Date: '.food-menu-date',
    BreakfastList: 'spreadsheet-control:nth-of-type(1)',
    LunchList: 'spreadsheet-control:nth-of-type(2)',
    DinnerList: 'spreadsheet-control:nth-of-type(3)',
    SnackList: 'spreadsheet-control:nth-of-type(4)',
  };

  static elements = {
    dateElement: this.selectors.Date,
    breakfastList: this.selectors.BreakfastList,
    lunchList: this.selectors.LunchList,
    dinnerList: this.selectors.DinnerList,
    snackList: this.selectors.SnackList,
  };

  setDate(date) {
    this.dateElement.textContent = this.dateFormat.format(new Date(date));
  }

  insertFoodList(meal, index, entry) {
    let listEl = this.#getListElement(meal);
    if (listEl) {
      listEl.insertRow(index, [entry.food, entry.quantity, entry.unit]);
    }
  }

  deleteFoodList(meal, index) {
    let listEl = this.#getListElement(meal);
    if (listEl) {
      listEl.deleteRow(index);
    }
  }

  changeFoodList(meal, index, entry) {
    let listEl = this.#getListElement(meal);
    if (listEl) {
      listEl.changeRow(index, [entry.food, entry.quantity, entry.unit]);
    }
  }

  initialize() {
    super.initialize();

    for (let meal of FoodMenu.meals) {
      let listEl = this.#getListElement(meal);
      listenToEvent(listEl, 'rowappend', ev =>
        this.dispatchEvent(
          new CustomEvent('food-add', {
            detail: {
              meal,
              index: ev.detail.rowIndex,
            },
            bubbles: true,
          })
        )
      );
      listenToEvent(listEl, 'rowdelete', ev =>
        this.dispatchEvent(
          new CustomEvent('food-delete', {
            detail: {
              meal,
              index: ev.detail.rowIndex,
            },
            bubbles: true,
          })
        )
      );
      listenToEvent(listEl, 'rowchange', ev => {
        const detail = {
          meal,
          index: ev.detail.rowIndex,
        };
        switch (ev.detail.columnIndex) {
          case 0:
            detail.food = ev.detail.value;
            break;
          case 1:
            detail.quantity = ev.detail.value;
            break;
          case 2:
            detail.unit = ev.detail.value;
            break;
        }
        this.dispatchEvent(
          new CustomEvent('food-change', {
            detail,
            bubbles: true,
          })
        );
      });
    }
  }

  get dateFormat() {
    if (!this.#dateFormat) {
      const options = {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      };
      this.#dateFormat = new Intl.DateTimeFormat('en-US', options);
    }
    return this.#dateFormat;
  }

  #getListElement(meal) {
    switch (meal) {
      case 'breakfast':
        return this.breakfastList;
      case 'lunch':
        return this.lunchList;
      case 'dinner':
        return this.dinnerList;
      case 'snacks':
        return this.snackList;
      default:
        error(`Unexpected ${meal} meal given`);
    }
    return null;
  }

  static getMealMarkup(label) {
    return `
      <div class='food-menu-meal'>${label}</div>
      <spreadsheet-control colcount='3'>
        <template>
          <input class='spreadsheet-cell'
                 placeholder='Food'
                 list='food-menu-foods'>
          <input type='number'
                 class='spreadsheet-cell'
                 placeholder='Qty'
                 min='0'>
          <input class='spreadsheet-cell'
                 placeholder='Unit'
                 list='food-menu-units'>
        </template>
      </spreadsheet-control>`;
  }

  static markup = `
    <div class='food-menu-date'></div>
    ${this.getMealMarkup('Breakfast')}
    ${this.getMealMarkup('Lunch')}
    ${this.getMealMarkup('Dinner')}
    ${this.getMealMarkup('Snacks')}
  `;

  #dateFormat;
}

window.customElements.define('food-menu-control', FoodMenuControl);

export { FoodMenuControl };
